import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Navbar } from "@/components/layout/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import analytics from "@/lib/segment";
import { BookOpen, BarChart3, Shield, Clock, CheckCircle2, ArrowRight, Star, AlertTriangle, MessageCircle, TrendingUp, GitBranch, Target, ClipboardCheck, Mail } from "lucide-react";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { JsonLd, organizationSchema, websiteSchema, softwareApplicationSchema } from "@/components/seo/JsonLd";

const features = [{
  icon: BookOpen,
  title: "Expert Playbooks",
  description: "Step-by-step guides for damage claims, late returns, and more. Never feel lost again."
}, {
  icon: BarChart3,
  title: "Business Dashboard",
  description: "See your true profit per vehicle. Know which cars make money and which don't."
}, {
  icon: Shield,
  title: "Risk Protection",
  description: "Choose the right protection plan and off-rent insurance. Get paid for incidents."
}, {
  icon: Clock,
  title: "Save Hours Weekly",
  description: "Eliminate decision paralysis. Know exactly when to do what. Add clarity to your business operations."
}];

const testimonials = [{
  quote: "Finally, a tool built by hosts, for hosts. The damage claim playbook saved me $2,000.",
  author: "Marcus T.",
  role: "12-car fleet owner",
  rating: 5
}, {
  quote: "I had no idea two of my cars were actually losing money until I saw the P&L breakdown.",
  author: "Sarah K.",
  role: "Part-time host",
  rating: 5
}];

const problemColumns = [{
  icon: AlertTriangle,
  title: "Decision Paralysis",
  copy: "A guest returns a car 2 hours late with a scratch on the rim. Do you report it immediately? Do you charge a late fee first? Making the wrong move now could cost you the entire claim."
}, {
  icon: MessageCircle,
  title: "Unreliable Information",
  copy: "You turn to Facebook groups or Reddit for help, but you get ten different answers. Some are outdated, some are dangerous. You need verified procedures, not opinions."
}, {
  icon: TrendingUp,
  title: "Financial Uncertainty",
  copy: "You see revenue coming in, but between maintenance, depreciation, and loan interest, do you know if that specific car is actually profitable? Or are you just churning cash?"
}];

export default function TuroHostTools() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/app/dashboard", { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    try {
      // Store email in session storage for the survey page
      sessionStorage.setItem("waitlist_email", email);

      // Submit to ActiveCampaign API with Waitlist_Discount tag
      const {
        data,
        error
      } = await supabase.functions.invoke("subscribe-activecampaign", {
        body: {
          email
        }
      });
      if (error) {
        console.error("Error subscribing to ActiveCampaign:", error);
        toast.error("Something went wrong. Please try again.");
        setIsSubmitting(false);
        return;
      }
      console.log("ActiveCampaign subscription response:", data);

      // Track email subscription
      analytics.track("Email list subscribed", {
        source_location: "hero_waitlist",
        email: email,
      });

      // Redirect to the onboarding survey
      navigate(`/onboarding-survey?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error("Error in waitlist submission:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return <div className="min-h-screen">
      <JsonLd data={[organizationSchema, websiteSchema, softwareApplicationSchema]} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-hero text-white">
        <div className="container mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm mb-8">
              <AnchorIcon className="h-4 w-4" />
              <span>The command center for Turo hosts</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Stop Guessing.
              <br />
              <span className="text-gradient">Start Profiting.</span>
            </h1>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              HostCaptain gives you the playbooks, dashboards, and tools to run your Turo business like a pro.
              <br /><br />
              Handle any situation. Know your real profit. Remove the guesswork.
            </p>
            <form id="hero-email-form" onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input 
                    type="email" 
                    placeholder="Enter your email address" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    className="pl-10 py-6 bg-white text-foreground border-0" 
                    required 
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="bg-gradient-primary hover:opacity-90 px-6 py-6" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Starting..." : (
                    <>
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
            <p className="text-sm text-white/60 mt-6">
              Free to start. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Transparency Block */}
      <section className="py-20 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              We Are Building the Ultimate Co-Pilot for Hosts
            </h2>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              Most software is built by developers who have never washed a car or dealt with a late return. We are different. We are building HostCaptain to solve the real problems hosts face every day, and we want your input to ensure it solves the problems you've faced.
            </p>
            <p 
              className="text-xl font-semibold text-primary cursor-pointer hover:underline transition-all" 
              onClick={() => {
                const element = document.getElementById("hero-email-form");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                  const input = element.querySelector("input");
                  if (input) input.focus();
                }
              }}
            >
              Join us early to shape the features that will save your sanity.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: The Problem */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              The "Unknowns" Are Costing You Money.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {problemColumns.map((column, index) => <Card key={index} className="border-border bg-card hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                    <column.icon className="h-8 w-8 text-destructive" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">{column.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{column.copy}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Section 4: The Solution */}
      <section id="solution-section" className="py-24 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Navigate Every Scenario with Confidence.
            </h2>
          </div>

          {/* Feature 1: Expert Playbooks */}
          <div className="max-w-6xl mx-auto mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="bg-card rounded-xl border border-border p-6 shadow-lg">
                  <div className="bg-primary/10 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-primary mb-2">Decision Tree</p>
                    <p className="font-semibold text-foreground">Damage Claim Wizard</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">1</div>
                      <span className="text-foreground">Do you have photos from within 24 hours before the trip?</span>
                      <div className="ml-auto flex gap-2">
                        <span className="px-3 py-1 bg-accent/20 text-accent rounded text-sm">Yes</span>
                        <span className="px-3 py-1 bg-muted-foreground/20 text-muted-foreground rounded text-sm">No</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">2</div>
                      <span className="text-foreground">Have you gotten an estimate from a mechanic and body shop?</span>
                      <div className="ml-auto flex gap-2">
                        <span className="px-3 py-1 bg-muted-foreground/20 text-muted-foreground rounded text-sm">Yes</span>
                        <span className="px-3 py-1 bg-muted-foreground/20 text-muted-foreground rounded text-sm">No</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-accent/10 rounded-lg border border-accent/30">
                      <GitBranch className="h-5 w-5 text-accent" />
                      <span className="text-accent font-medium">Next: Document damage photos...</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">Your Expert Guide for High-Stress Moments</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Stop guessing. Our interactive Decision Trees walk you through complex scenarios step-by-step. From handling late returns to filing a damage claim, simply follow the prompts to ensure you never miss a critical step.
                </p>
              </div>
            </div>
          </div>

          {/* Feature 2: Car Profitability */}
          <div className="max-w-6xl mx-auto mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Watch Your Cars Pay for Themselves</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  <Link to="/docs/getting-started-earnings" className="text-primary hover:underline">Upload your Turo CSVs</Link>, <Link to="/docs/getting-started-expenses" className="text-primary hover:underline">enter your expenses</Link>, and see your P&L for each vehicle. Track exactly when each car crosses the break-even point and starts generating pure profit.
                </p>
              </div>
              <div>
                <div className="bg-card rounded-xl border border-border p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">2017 Chevy Equinox</p>
                      <p className="text-xl font-bold text-foreground">Return on Investment</p>
                    </div>
                    <Target className="h-8 w-8 text-primary" />
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Paid: $12,870</span>
                      <span className="text-foreground font-medium">67%</span>
                    </div>
                    <div className="h-4 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full" style={{
                      width: '67%'
                    }}></div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">$4,247 remaining to break-even</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Net</p>
                      <p className="text-lg font-semibold text-accent">+$847</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Est. Pay-Off</p>
                      <p className="text-lg font-semibold text-foreground">Nov 2026</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3: Survival Checklist */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="bg-card rounded-xl border border-border p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <ClipboardCheck className="h-6 w-6 text-primary" />
                    <p className="font-semibold text-foreground">Check-In Photo Checklist</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-accent/10 rounded-lg border border-accent/30">
                      <CheckCircle2 className="h-5 w-5 text-accent" />
                      <span className="text-foreground">Step 1: Exterior photos (including bottom)</span>
                      <span className="ml-auto text-xs bg-accent text-accent-foreground px-2 py-1 rounded">Complete</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-accent/10 rounded-lg border border-accent/30">
                      <CheckCircle2 className="h-5 w-5 text-accent" />
                      <span className="text-foreground">Step 2: Odometer & fuel level photo</span>
                      <span className="ml-auto text-xs bg-accent text-accent-foreground px-2 py-1 rounded">Complete</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
                      <div className="w-5 h-5 rounded-full border-2 border-primary"></div>
                      <span className="text-foreground">Step 3: Interior & trunk inspection</span>
                      <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-1 rounded">In Progress</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg opacity-60">
                      <div className="w-5 h-5 rounded-full border-2 border-muted-foreground"></div>
                      <span className="text-muted-foreground">Step 4: Guest walkthrough sign-off</span>
                      <span className="ml-auto text-xs bg-muted-foreground/20 text-muted-foreground px-2 py-1 rounded">Waiting</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">Win Claims Before They Happen</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Our Checklists ensure you capture the right evidence every time. Whether it's a <Link to="/docs/checkin-checkout-process" className="text-primary hover:underline">check-in photo strategy</Link> or an incident report, we make sure you have the documentation Turo requires to pay out.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From your first trip to scaling to 10+ cars, HostCaptain has you covered.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => <Card key={index} className="border-border bg-card hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-24 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Trusted by Turo Hosts
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => <Card key={index} className="bg-card">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-5 w-5 fill-warning text-warning" />)}
                  </div>
                  <p className="text-foreground mb-4 text-lg">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-hero text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Take Control?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Join us and upgrade from spreadsheets to HostCaptain.
          </p>
          
          <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input 
                  type="email" 
                  placeholder="Enter your email address" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  className="pl-10 py-6 bg-white text-foreground border-0" 
                  required 
                />
              </div>
              <Button 
                type="submit" 
                size="lg" 
                className="bg-gradient-primary hover:opacity-90 px-6 py-6" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Starting..." : (
                  <>
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
          
          <p className="text-sm text-white/60 mt-8 max-w-lg mx-auto">
            Free to start. No credit card required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <AnchorIcon className="h-6 w-6" />
              <span className="font-bold">HostCaptain</span>
            </div>
            <div className="flex gap-8 text-sm text-white/60">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/auth" className="hover:text-white transition-colors">Login</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-white/40">
            © {new Date().getFullYear()} HostCaptain. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}
